import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { ArrowLeft, ShieldCheck, Lock } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { ExternalCheckout } from "@/components/ExternalCheckout";

const Field = ({ label, ...props }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.12em] text-brown-dark/60 font-semibold mb-1.5">{label}</label>
    <input {...props} className="w-full border border-gold/30 rounded-lg px-3.5 py-3 focus:border-gold focus:outline-none transition-colors" />
  </div>
);

const Checkout = () => {
  const navigate = useNavigate();
  const { items, total, clearCart, checkoutConfig: config } = useCart();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const valid = name.trim().length > 1 && /\S+@\S+\.\S+/.test(email);

  if (!config) return <div className="min-h-[60vh] flex items-center justify-center" data-testid="checkout-loading">Chargement…</div>;
  if (!config.onsite_checkout_enabled) return <ExternalCheckout items={items} />;

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center">
        <p className="text-brown-black/60">Votre panier est vide.</p>
        <button onClick={() => navigate("/boutique")} className="text-gold font-semibold hover:underline mt-4">Retour à la boutique</button>
      </div>
    );
  }

  const createOrder = async () => {
    if (!valid) {
      toast.error("Veuillez renseigner votre nom et votre e-mail.");
      throw new Error("invalid");
    }
    const payload = {
      customer_name: name,
      customer_email: email,
      items: items.map((i) => ({ product_id: i.id, variant_label: i.variant_label || "", qty: i.qty })),
    };
    const r = await api.post("/orders", payload);
    return r.data.paypal_order_id;
  };

  const onApprove = async (data) => {
    try {
      const r = await api.post(`/orders/${data.orderID}/capture`);
      clearCart();
      navigate("/commande-confirmee", { state: { ...r.data, email } });
    } catch (e) {
      toast.error("Le paiement n'a pas pu être confirmé.");
      navigate("/paiement-annule");
    }
  };

  return (
    <div className="bg-ivory min-h-[70vh]">
      <Seo title="Paiement sécurisé" description="Finalisez votre commande." path="/checkout" noindex />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <button onClick={() => navigate("/boutique")} className="inline-flex items-center gap-2 text-brown-black/60 hover:text-gold text-sm font-semibold mb-8">
          <ArrowLeft size={16} /> Continuer mes achats
        </button>
        <h1 className="font-heading text-3xl md:text-4xl font-light text-brown-black mb-8">Paiement</h1>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Form + PayPal */}
          <div className="order-2 lg:order-1">
            <div className="bg-white rounded-2xl border border-gold/20 p-6 space-y-4">
              <h2 className="font-heading text-xl text-brown-black">Vos informations</h2>
              <Field label="Prénom et nom" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex : Jean Dupont" data-testid="checkout-name" />
              <Field label="Adresse e-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Pour recevoir votre téléchargement" data-testid="checkout-email" />
              <p className="text-xs text-brown-black/50">Vos liens de téléchargement sécurisés seront disponibles immédiatement après paiement et envoyés par e-mail.</p>

              <div className="pt-2" data-testid="checkout-paypal-zone">
                {config === null ? (
                  <p className="text-brown-black/50 text-sm">Chargement du paiement…</p>
                ) : !config.configured ? (
                  <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800" data-testid="paypal-not-configured">
                    Le paiement PayPal n'est pas encore activé. Les clés PayPal doivent être ajoutées par l'administrateur.
                  </div>
                ) : !valid ? (
                  <div className="rounded-xl bg-ivory border border-gold/20 p-4 text-sm text-brown-black/60 flex items-center gap-2">
                    <Lock size={15} className="text-gold" /> Renseignez votre nom et e-mail pour afficher le bouton de paiement.
                  </div>
                ) : (
                  <PayPalScriptProvider options={{ clientId: config.client_id, currency: config.currency, intent: "capture" }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal" }}
                      createOrder={createOrder}
                      onApprove={onApprove}
                      onCancel={() => navigate("/paiement-annule")}
                      onError={() => toast.error("Une erreur est survenue avec PayPal.")}
                    />
                  </PayPalScriptProvider>
                )}
              </div>
              <p className="flex items-center gap-2 text-xs text-brown-black/55">
                <ShieldCheck size={14} className="text-gold" /> Paiement traité de façon sécurisée par PayPal. Aucune donnée bancaire n'est stockée par le site.
              </p>
            </div>
          </div>

          {/* Summary */}
          <div className="order-1 lg:order-2">
            <div className="bg-white rounded-2xl border border-gold/20 p-6">
              <h2 className="font-heading text-xl text-brown-black mb-4">Récapitulatif</h2>
              <ul className="space-y-4">
                {items.map((i) => (
                  <li key={i.key} className="flex gap-4">
                    <img src={i.image} alt={i.name} className="w-16 h-16 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="font-heading text-base text-brown-black truncate">{i.name}</p>
                      {i.variant_label && <p className="text-[11px] text-brown-black/50">{i.variant_label}</p>}
                      <p className="text-sm text-brown-black/60">Quantité : {i.qty}</p>
                    </div>
                    <span className="font-semibold text-brown-dark">{(i.price * i.qty).toFixed(2)} €</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-gold/15 mt-5 pt-5 space-y-2">
                <div className="flex justify-between text-sm text-brown-black/70"><span>Sous-total</span><span>{total.toFixed(2)} €</span></div>
                <div className="flex justify-between text-sm text-brown-black/70"><span>Livraison</span><span>Gratuite (numérique)</span></div>
                <div className="flex justify-between items-center pt-2">
                  <span className="font-heading text-lg text-brown-black">Total</span>
                  <span className="font-heading text-2xl font-semibold text-gold" data-testid="checkout-total">{total.toFixed(2)} €</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
